import { useEffect, useState } from 'react';

/**
 * Cross-cutting "session expired" signal.
 *
 * <p>Why this exists: when the backend rejects a request with 401 (cookie TTL
 * elapsed, server-side session revoked, etc.), every concurrent request on
 * the page may receive the same 401 in quick succession — but the user should
 * see exactly ONE dialog, not N. This module coalesces those triggers into a
 * single boolean flag that any subscriber can read.
 *
 * <p>Pure logic / framework-agnostic — the React hook {@link useSessionExpired}
 * is exposed for convenience but the {@link sessionExpiredStore} can be used
 * outside React too.
 */

type Listener = () => void;
const listeners = new Set<Listener>();
let expired = false;
/** The path the user was on when the session died — surfaced as ?returnTo on /login. */
let capturedReturnTo: string | null = null;

export const sessionExpiredStore = {
  /**
   * Mark the session as expired. Idempotent — a second call while still
   * expired is a no-op (this is what makes concurrent 401s coalesce into one
   * dialog).
   *
   * <p>Captures the current pathname + search the first time it's triggered
   * so the post-login navigation can deep-link back where the user was.
   */
  trigger(): void {
    if (expired) return;
    expired = true;
    try {
      if (typeof window !== 'undefined') {
        const { pathname, search } = window.location;
        // Don't loop back to /login itself.
        if (!pathname.startsWith('/login')) {
          capturedReturnTo = `${pathname}${search}`;
        }
      }
    } catch {
      // SSR / non-browser context — ignore
    }
    listeners.forEach((l) => l());
  },

  /** Clear the flag — called by the dialog after the user acknowledges. */
  reset(): void {
    if (!expired) return;
    expired = false;
    capturedReturnTo = null;
    listeners.forEach((l) => l());
  },

  isExpired(): boolean {
    return expired;
  },

  /** The path the user was on when the session died, or null if not captured. */
  returnTo(): string | null {
    return capturedReturnTo;
  },

  /** React/non-React subscribe — returns an unsubscribe function. */
  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

/**
 * React hook for the session-expired flag.
 * Re-renders the calling component whenever the flag toggles.
 */
export function useSessionExpired(): boolean {
  const [expiredState, setExpiredState] = useState<boolean>(sessionExpiredStore.isExpired());
  useEffect(() => {
    return sessionExpiredStore.subscribe(() => {
      setExpiredState(sessionExpiredStore.isExpired());
    });
  }, []);
  return expiredState;
}
